<template>
  <div>
    <slot />
    <slot name="defaultFields">
      <div v-if="showEisenhowerPriority">
        <!-- EISENHOWER-PRIORITY -->
        <EventActionFieldRowComponent :label="t('components.issueModal.eisenhowerPriority')">
          <InputRow v-model="event.eisenhowerPriority" name="eisenhowerPriority">
            <EisenhowerSelector
              v-model="event.eisenhowerPriority"
              :disabled="disable.eisenhowerPriority || disableEvent"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </div>
      <div>
        <EventActionFieldRowComponent :label="t('components.issueModal.area')">
          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            :model-value="flags"
            name="flags"
            hide-label
            :disabled="disable.flags || disableEvent"
            :display-name="isLISA3 ? t('components.issueModal.area') : undefined"
            :required="event.type === IssueType.SBBQuality"
            :rules="event.type === IssueType.SBBQuality ? [minItems(3)] : []"
          >
            <SearchSelect
              v-bind="inputField"
              v-model="flags"
              :state="valid"
              :mode="SelectMode.FLAG"
              class="w-100"
              :filters="{
                siteIds: siteId,
              }"
              :exclude="excludeFromSearch"
              :multiple="true"
              :disabled="disable.flags || disableEvent"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </div>

      <div>
        <EventActionFieldRowComponent :label="t('components.issueModal.user')">
          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            v-model="event.assigneeId"
            name="assigneeId"
            hide-label
            required
          >
            <SearchSelect
              v-bind="inputField"
              v-model="event.assigneeId"
              :mode="SelectMode.USER"
              :state="valid"
              :disabled="disable.creator || disableEvent"
              :filters="{
                availableOnSiteIds: siteId == null ? undefined : [siteId],
              }"
              @update:model-value="$emit('assigneeChanged', $event)"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </div>

      <div>
        <EventActionFieldRowComponent :label="t('components.issueModal.date')">
          <InputRow
            v-slot="{ field: inputField }"
            v-model="deadline"
            name="deadline"
            hide-label
            required
            :rules="[validDate()]"
          >
            <DatePickerComponentNew
              v-bind="inputField"
              v-model="deadline"
              :label="t('components.issueModal.date')"
              :disabled="disable.date || disableEvent"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </div>
      <div>
        <EventActionFieldRowComponent :label="t('components.issueModal.description')">
          <InputRow
            v-slot="{ field: inputField, validation: { valid } }"
            v-model="event.description"
            name="description"
            required
            hide-label
            :rules="[minLength(1)]"
            :display-name="t('components.issueModal.description')"
            type="textarea"
            :disabled="disable.description || disableEvent"
            rows="4"
            auto-focus
            @keydown.enter="handleEnterKeyPress"
          >
            <InputTextarea
              v-bind="inputField"
              id="IssueFieldsModalComponentDescriptionField"
              v-model="event.description"
              class="w-100"
              data-testid="IssueFieldsModalComponentDescriptionField"
              auto-resize
              :disabled="disable.description || disableEvent"
              rows="4"
              :state="valid"
              autofocus
              @keydown.enter="handleEnterKeyPress"
            />
          </InputRow>
        </EventActionFieldRowComponent>
      </div>
    </slot>
    <slot name="trailing" />
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { minItems, minLength, validDate } from "@/modules/formValidation";
import InputRow from "@/components/shared/form/InputRow.vue";
import { computed, type PropType } from "vue";
import InputTextarea from "primevue/textarea";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import injectAsserted from "@/functions/assert/injectAsserted";
import { DisableEventInjectionKey } from "../../issue/IssueTypes";
import DatePickerComponentNew from "@/components/shared/DatePickerComponentNew.vue";
import { AbstractIssueModalComponentFields } from "../EventHelper";
import EventActionFieldRowComponent from "../../EventActionFieldRowComponent.vue";
import type { IssueEditType } from "./EventEditType";
import EisenhowerSelector from "../eisenhowerMatrix/EisenhowerSelector.vue";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { useSQDCGlobalSettings } from "@/modules/sqdc/helper/useSQDCGlobalSettings";
import { DateTime } from "luxon";
import { IssueType } from "@/graphql.ts";
import { isLISA3 } from "@/composables/useLISA3.ts";

defineOptions({
  inheritAttrs: true,
});
const props = defineProps({
  event: {
    type: Object as PropType<IssueEditType>,
    required: true,
  },
  excludeFromSearch: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  disable: {
    type: Object as PropType<Partial<Record<AbstractIssueModalComponentFields, boolean>>>,
    default: () => ({
      description: false,
      owner: false,
      date: false,
      flags: false,
      creator: false,
      eisenhowerPriority: false,
    }),
  },
  descriptionText: {
    type: String,
    default: null,
  },
  flagTypes: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  labelCol: {
    type: Number,
    default: 2,
  },
});
const emit = defineEmits(["save", "assigneeChanged"]);
const disableEvent = injectAsserted(DisableEventInjectionKey);

const flags = computed({
  get() {
    if (props.event.flags == null) {
      return [];
    }
    return props.event.flags.map((flag) => flag.id);
  },
  set(val) {
    props.event.flags = val.map((id) => ({ id }) as { id: number });
  },
});

const { siteId } = useProvidedBoardContext();

const deadline = computed({
  get(): DateTime {
    return DateTime.fromJSDate(new Date(props.event.deadline));
  },
  set(val: DateTime) {
    props.event.deadline = val.toISO();
  },
});

function handleEnterKeyPress(e: KeyboardEvent) {
  if (e.metaKey || e.ctrlKey) {
    emit("save", e.shiftKey);
  }
}

const { showEisenhowerPriority } = useSQDCGlobalSettings();

const { t } = useTranslation();
</script>

<style lang="scss" scoped></style>
